/**
 * IntegrationControlCenter.jsx — AOS-PAY-UNIFIED-03A + 03B
 * Finance & Payments Integration Control Center
 *
 * Roles: institution_admin, super_admin (configure)
 *        finance_manager, cfo (view + execute)
 *
 * ARGOV-AI-VALUE-GATE-01: Deterministic validation only. No AI invoked.
 * INV-ICC-SECRET-01: Secret values never returned or displayed after storage.
 * INV-03B-ASYNC-01: Document POSTED requires explicit ERP acknowledgement.
 * INV-03B-ARGOV-01: Journal sync requires human admin authorized=true.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  CheckCircle, XCircle, AlertTriangle, Plug, RefreshCw,
  Shield, Database, Layers, Activity, ChevronRight,
  ChevronDown, Settings, Eye, EyeOff, Play, Pause,
  RotateCcw, Info, Clock, Server, Lock, Grid,
  ArrowRight, FileCheck, AlertCircle, Terminal, Wifi, WifiOff,
  List, Zap, BarChart2,
} from "lucide-react";
import { toast } from "../components/ui/sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Provider definitions ──────────────────────────────────────────────────────
const PROVIDERS = [
  {
    id: "academiaos_native",
    provider: "test_erp",
    category: "ERP",
    label: "AcademiaOS Native Finance",
    icon: Database,
    description: "Built-in finance engine — GL, AR, AP, reconciliation. End-to-end certified via FM7 test adapters.",
    certTier: "CERTIFIED",
    certColor: "text-emerald-400",
    certBg: "bg-emerald-900/30 border-emerald-700/50",
    canConfigure: true,
  },
  {
    id: "oracle_fusion",
    provider: "oracle_erp",
    category: "ERP",
    label: "Oracle Fusion Financials",
    icon: Server,
    description: "Enterprise ERP integration. Real adapter and sandbox certification pending (Phase 03B).",
    certTier: "NOT_CONNECTED",
    certColor: "text-amber-400",
    certBg: "bg-amber-900/30 border-amber-700/50",
    canConfigure: true,
    hasConnector: true,
  },
  {
    id: "sap_s4hana",
    provider: "sap",
    category: "ERP",
    label: "SAP S/4HANA",
    icon: Layers,
    description: "SAP enterprise integration. Real adapter and sandbox certification pending (Phase 03B).",
    certTier: "NOT_CONNECTED",
    certColor: "text-amber-400",
    certBg: "bg-amber-900/30 border-amber-700/50",
    canConfigure: true,
    hasConnector: true,
  },
  {
    id: "generic_erp",
    provider: "dynamics",
    category: "ERP",
    label: "Generic ERP Connector",
    icon: Grid,
    description: "Generic connector for Dynamics 365, Tally, and other ERPs. No concrete endpoint contract validated.",
    certTier: "NOT_CERTIFIED",
    certColor: "text-slate-400",
    certBg: "bg-slate-900/30 border-slate-700/50",
    canConfigure: false,
  },
];

const OWNERSHIP_OBJECTS = [
  { key: "student_receivables",   label: "Student Receivables (AR)",    icon: "💰" },
  { key: "applicant_receivables", label: "Applicant Receivables (AR)",   icon: "📝" },
  { key: "collections",           label: "Collections Enforcement",      icon: "🏦" },
  { key: "receipt_issuance",      label: "Receipt Issuance",             icon: "🧾" },
  { key: "refunds",               label: "Refunds & Reversals",          icon: "↩️" },
  { key: "accounts_payable",      label: "Accounts Payable (AP)",        icon: "📤" },
  { key: "general_ledger",        label: "General Ledger (GL)",          icon: "📒" },
  { key: "reconciliation",        label: "Reconciliation",               icon: "⚖️" },
];

const OWNER_OPTIONS = [
  { value: "academiaos", label: "AcademiaOS", color: "text-blue-400" },
  { value: "erp",        label: "External ERP", color: "text-purple-400" },
  { value: "shared",     label: "Shared", color: "text-amber-400" },
];

const ACCOUNT_KEYS = [
  "student_receivables", "applicant_receivables", "tuition_revenue",
  "hostel_revenue", "application_fee_revenue", "scholarship_expense",
  "refund_liability", "accounts_payable", "bank_clearing", "suspense_account",
];

// ── Helpers ───────────────────────────────────────────────────────────────────
const authHeaders = () => {
  const token = localStorage.getItem("academiaos_token");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
};

const CertBadge = ({ tier }) => {
  const map = {
    CERTIFIED:       { label: "Certified",       cls: "bg-emerald-900/40 text-emerald-300 border-emerald-700/50" },
    NOT_CONNECTED:   { label: "Not Connected",   cls: "bg-amber-900/40  text-amber-300  border-amber-700/50" },
    NOT_CERTIFIED:   { label: "Not Certified",   cls: "bg-slate-800/60  text-slate-400  border-slate-600/50" },
    CONFIGURED_READY:{ label: "Config Ready",    cls: "bg-blue-900/40   text-blue-300   border-blue-700/50" },
  };
  const { label, cls } = map[tier] || map.NOT_CERTIFIED;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold border ${cls}`}>
      {label}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const colors = {
    ACTIVE: "bg-emerald-400", CONFIGURED: "bg-blue-400",
    ERROR: "bg-red-400", DISABLED: "bg-slate-500",
    VALIDATING: "bg-amber-400", DEGRADED: "bg-amber-400",
    NOT_CONFIGURED: "bg-slate-600",
  };
  return (
    <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || "bg-slate-500"}`} />
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export default function IntegrationControlCenter() {
  const [health, setHealth]           = useState(null);
  const [loadingHealth, setLH]        = useState(true);
  const [selected, setSelected]       = useState(null); // selected provider def
  const [activeTab, setActiveTab]     = useState("overview");
  const [cfgId, setCfgId]             = useState(null); // current integration_configuration id
  const [profile, setProfile]         = useState(null);
  const [ownership, setOwnership]     = useState({});
  const [mappings, setMappings]       = useState({});
  const [accounts, setAccounts]       = useState({});
  const [inboxEvents, setInboxEvents] = useState([]);
  const [execHistory, setExecHistory] = useState([]);
  const [valResult, setValResult]     = useState(null);
  const [loadingTab, setLoadingTab]   = useState(false);
  const [saving, setSaving]           = useState(false);
  const [approving, setApproving]     = useState(false);
  const [testing, setTesting]         = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [secretRef, setSecretRef]     = useState("");
  const [showSecretInput, setShowSecretInput] = useState(false);
  const [createMode, setCreateMode]   = useState(false);
  // 03B connector state
  const [connectorConfig, setConnectorConfig] = useState({});
  const [connectorSchema, setConnectorSchema] = useState(null);
  const [connectorTestResult, setConnectorTestResult] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [connectorEvents, setConnectorEvents] = useState([]);
  const [certSteps, setCertSteps]     = useState(null);
  const [savingConnector, setSavingConnector] = useState(false);
  const [testingConnector, setTestingConnector] = useState(false);

  // Load health on mount
  useEffect(() => {
    loadHealth();
  }, []);

  const loadHealth = async () => {
    setLH(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/health`, { headers: authHeaders() });
      if (r.ok) setHealth(await r.json());
    } catch { /* non-blocking */ }
    finally { setLH(false); }
  };

  // Find existing configuration for a provider
  const findConfig = (provider) => {
    if (!health?.configurations) return null;
    return health.configurations.find(c => c.provider === provider) || null;
  };

  const selectProvider = async (prov) => {
    setSelected(prov);
    setActiveTab("overview");
    setValResult(null);
    setProfile(null);
    setOwnership({});
    setMappings({});
    setAccounts({});
    setInboxEvents([]);
    setExecHistory([]);

    const existing = findConfig(prov.provider);
    if (existing) {
      setCfgId(existing.id);
      setCreateMode(false);
      await loadProfile(existing.id);
    } else {
      setCfgId(null);
      setCreateMode(true);
    }
  };

  const loadProfile = async (id) => {
    setLoadingTab(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${id}/profile`, { headers: authHeaders() });
      if (r.ok) {
        const data = await r.json();
        setProfile(data);
        setOwnership(data.ownership_json || {});
        const fm = data.field_mapping_json || {};
        setMappings({ legal_entity: fm.legal_entity || "", ledger: fm.ledger || "",
                      cost_center: fm.cost_center || "", erp_company_code: fm.erp_company_code || "",
                      erp_business_unit: fm.erp_business_unit || "", fiscal_year_format: fm.fiscal_year_format || "" });
        setAccounts(fm.accounts || {});
      }
    } finally { setLoadingTab(false); }
  };

  const loadTabData = async (tab) => {
    if (!cfgId) return;
    setLoadingTab(true);
    try {
      if (tab === "events") {
        const [inboxR, execR] = await Promise.all([
          fetch(`${API}/api/finance/payment-gateway/inbox?limit=30`, { headers: authHeaders() }),
          fetch(`${API}/api/finance/integrations/${cfgId}/executions?limit=30`, { headers: authHeaders() }),
        ]);
        if (inboxR.ok) setInboxEvents((await inboxR.json()).inbox_events || []);
        if (execR.ok) setExecHistory((await execR.json()).executions || []);
      } else if (tab === "connector") {
        const [cfgR, capR, stepsR] = await Promise.all([
          fetch(`${API}/api/finance/erp-connectors/${cfgId}/connector-config`, { headers: authHeaders() }),
          fetch(`${API}/api/finance/erp-connectors/${cfgId}/capabilities`, { headers: authHeaders() }),
          fetch(`${API}/api/finance/erp-connectors/${cfgId}/certification-steps`, { headers: authHeaders() }),
        ]);
        if (cfgR.ok) {
          const d = await cfgR.json();
          setConnectorConfig(d.connector_config || {});
          setConnectorSchema(d.schema);
        }
        if (capR.ok) setCapabilities((await capR.json()).capabilities);
        if (stepsR.ok) setCertSteps(await stepsR.json());
      } else if (tab === "sync") {
        const r = await fetch(`${API}/api/finance/erp-connectors/${cfgId}/events?limit=30`, { headers: authHeaders() });
        if (r.ok) setConnectorEvents((await r.json()).events || []);
      }
    } finally { setLoadingTab(false); }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "events") loadTabData("events");
  };

  // Create new configuration
  const createConfig = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          category: selected.category,
          provider: selected.provider,
          display_name: selected.label,
          config_json: {},
        }),
      });
      if (!r.ok) { toast.error("Failed to create configuration"); return; }
      const data = await r.json();
      toast.success("Integration profile created");
      setCfgId(data.integration_id);
      setCreateMode(false);
      await loadHealth();
      await loadProfile(data.integration_id);
    } finally { setSaving(false); }
  };

  // Save credential reference
  const saveCredentialRef = async () => {
    if (!cfgId || !secretRef.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ secret_reference: secretRef.trim() }),
      });
      if (r.ok) {
        toast.success("Credential reference saved");
        setShowSecretInput(false);
        await loadProfile(cfgId);
      } else toast.error("Failed to save credential reference");
    } finally { setSaving(false); }
  };

  // Save ownership matrix
  const saveOwnership = async () => {
    if (!cfgId) return;
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/ownership`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ ownership }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success("Ownership matrix saved");
        if (data.warnings?.length) {
          data.warnings.forEach(w => toast.warning(w, { duration: 6000 }));
        }
        await loadProfile(cfgId);
      } else toast.error(data.detail || "Failed to save ownership");
    } finally { setSaving(false); }
  };

  // Save mappings
  const saveMappings = async () => {
    if (!cfgId) return;
    setSaving(true);
    try {
      const fullMappings = { ...mappings, accounts };
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/mappings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ mappings: fullMappings }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(`Mappings saved (v${data.mapping_version})`);
        await loadProfile(cfgId);
      } else toast.error(data.detail || "Failed to save mappings");
    } finally { setSaving(false); }
  };

  // Run validation
  const runValidation = async () => {
    if (!cfgId) return;
    setTesting(true);
    setValResult(null);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/validate-config`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      setValResult(data);
      if (data.validation_result === "PASSED") toast.success("Validation passed");
      else if (data.validation_result === "WARNINGS") toast.warning("Validation passed with warnings");
      else toast.error("Validation failed — review checks below");
    } finally { setTesting(false); }
  };

  // Test connection
  const testConnection = async () => {
    if (!cfgId) return;
    setTesting(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/test`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok && data.status === "ACTIVE") toast.success(`Connection test passed: ${data.test_result?.message}`);
      else toast.error(`Connection test failed: ${data.test_result?.message || "Unknown error"}`);
      await loadProfile(cfgId);
    } finally { setTesting(false); }
  };

  // Approve configuration
  const approveConfig = async () => {
    if (!cfgId) return;
    setApproving(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/approve`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ notes: approvalNotes }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success("Configuration approved");
        setApprovalNotes("");
        await loadProfile(cfgId);
        await loadHealth();
      } else toast.error(data.detail || "Approval failed");
    } finally { setApproving(false); }
  };

  // Enable / disable
  const toggleActive = async () => {
    if (!cfgId || !profile) return;
    const endpoint = profile.is_active ? "disable" : "enable";
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/finance/integrations/${cfgId}/${endpoint}`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(profile.is_active ? "Integration disabled" : "Integration activated");
        await loadProfile(cfgId);
        await loadHealth();
      } else toast.error(data.detail || "Operation failed");
    } finally { setSaving(false); }
  };

  // Retry inbox event
  const retryEvent = async (eventId) => {
    try {
      const r = await fetch(`${API}/api/finance/payment-gateway/inbox/${eventId}/retry`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(`Retry queued (attempt ${data.retry_count})`);
        await loadTabData("events");
      } else toast.error(data.detail || "Retry failed");
    } catch { toast.error("Retry failed"); }
  };

  // 03B: Save connector configuration
  const saveConnectorConfig = async () => {
    if (!cfgId) return;
    setSavingConnector(true);
    try {
      const r = await fetch(`${API}/api/finance/erp-connectors/${cfgId}/connector-config`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({ connector_config: connectorConfig }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success("Connector configuration saved");
        await loadTabData("connector");
        await loadProfile(cfgId);
      } else toast.error(data.detail?.message || data.detail || "Save failed");
    } finally { setSavingConnector(false); }
  };

  // 03B: Test real ERP connection
  const testERPConnection = async () => {
    if (!cfgId) return;
    setTestingConnector(true);
    setConnectorTestResult(null);
    try {
      const r = await fetch(`${API}/api/finance/erp-connectors/${cfgId}/test-connection`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      setConnectorTestResult(data);
      if (r.ok && data.connection_result?.success) {
        toast.success(`Connection succeeded — ${data.certification_status}`);
        await loadTabData("connector");
        await loadProfile(cfgId);
      } else {
        const errMsg = data.connection_result?.error_message || data.detail || "Connection failed";
        toast.error(`Connection failed: ${errMsg}`);
      }
    } finally { setTestingConnector(false); }
  };

  // 03B: Retry connector event
  const retryConnectorEvent = async (eventId) => {
    try {
      const r = await fetch(`${API}/api/finance/erp-connectors/${cfgId}/events/${eventId}/retry`, {
        method: "POST", headers: authHeaders(),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(`Retry queued (attempt ${data.retry_count})`);
        await loadTabData("sync");
      } else toast.error(data.detail?.message || data.detail || "Retry failed");
    } catch { toast.error("Retry failed"); }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const summary = health?.summary;

  return (
    <div className="min-h-screen bg-[#0a0d14] text-slate-100" data-testid="integration-control-center">
      {/* Header */}
      <div className="border-b border-slate-800 bg-[#0d1117] px-6 py-5">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                <Plug size={16} className="text-blue-400" />
              </div>
              <h1 className="text-xl font-bold text-white tracking-tight">Integration Control Center</h1>
            </div>
            <p className="text-slate-400 text-sm ml-11">Finance & Payments Provider Configuration — AOS-PAY-UNIFIED-03A</p>
          </div>
          <button
            onClick={loadHealth}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm transition-colors"
            data-testid="btn-refresh-health"
          >
            <RefreshCw size={14} className={loadingHealth ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* Health Summary Bar */}
        {summary && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
            data-testid="integration-health-summary"
          >
            {[
              { label: "Configured", val: summary.total_configured, icon: Plug, color: "text-slate-300" },
              { label: "Active",     val: summary.active,            icon: CheckCircle, color: "text-emerald-400" },
              { label: "Errors",     val: summary.errors,            icon: XCircle, color: "text-red-400" },
              { label: "Pending Approval", val: summary.pending_approval, icon: Clock, color: "text-amber-400" },
              { label: "Exec Completed", val: summary.success_executions, icon: Activity, color: "text-blue-400" },
              { label: "Exec Failed", val: summary.failed_executions, icon: AlertTriangle, color: "text-orange-400" },
              { label: "Failed Events", val: summary.failed_inbox_events, icon: Terminal, color: "text-rose-400" },
            ].map(({ label, val, icon: Icon, color }) => (
              <div key={label} className="bg-[#0d1117] border border-slate-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon size={14} className={color} />
                  <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
                </div>
                <p className={`text-2xl font-bold ${color}`}>{val ?? 0}</p>
              </div>
            ))}
          </div>
        )}

        {/* Provider Grid */}
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Select Integration Provider
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="provider-grid">
            {PROVIDERS.map((prov) => {
              const existing = findConfig(prov.provider);
              const Icon = prov.icon;
              const isSelected = selected?.id === prov.id;
              return (
                <button
                  key={prov.id}
                  onClick={() => selectProvider(prov)}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    isSelected
                      ? "border-blue-500/60 bg-blue-900/10 ring-1 ring-blue-500/30"
                      : `${prov.certBg} hover:border-slate-600`
                  }`}
                  data-testid={`provider-tile-${prov.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      isSelected ? "bg-blue-600/30" : "bg-slate-800/60"
                    }`}>
                      <Icon size={18} className={prov.certColor} />
                    </div>
                    {existing && <StatusDot status={existing.status} />}
                  </div>
                  <p className="font-semibold text-white text-sm mb-1">{prov.label}</p>
                  <p className="text-xs text-slate-400 mb-3 leading-relaxed line-clamp-2">{prov.description}</p>
                  <CertBadge tier={prov.certTier} />
                  {existing && (
                    <p className="text-xs text-slate-500 mt-2">
                      Status: <span className="text-slate-300">{existing.status}</span>
                    </p>
                  )}
                  {!existing && prov.canConfigure && (
                    <p className="text-xs text-slate-600 mt-2">Not configured</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Configuration Panel */}
        {selected && (
          <div className="bg-[#0d1117] border border-slate-800 rounded-xl overflow-hidden" data-testid="config-panel">
            {/* Panel header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
              <div className="flex items-center gap-3">
                <selected.icon size={18} className={selected.certColor} />
                <div>
                  <h3 className="font-semibold text-white">{selected.label}</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <CertBadge tier={selected.certTier} />
                    {profile && (
                      <>
                        <span className="text-slate-600 text-xs">·</span>
                        <StatusDot status={profile.status} />
                        <span className="text-xs text-slate-400">{profile.status}</span>
                        {profile.is_active && (
                          <span className="ml-1 px-1.5 py-0.5 bg-emerald-900/40 text-emerald-400 text-xs rounded border border-emerald-700/40">
                            ACTIVE
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              {profile && (
                <div className="flex items-center gap-2">
                  {profile.approved_at && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400">
                      <FileCheck size={12} /> Approved
                    </span>
                  )}
                  <button
                    onClick={toggleActive}
                    disabled={saving || !profile.approved_at}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all font-medium ${
                      profile.is_active
                        ? "bg-red-900/30 text-red-300 border border-red-700/40 hover:bg-red-900/50"
                        : profile.approved_at
                          ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/40 hover:bg-emerald-900/50"
                          : "bg-slate-800 text-slate-500 border border-slate-700 cursor-not-allowed"
                    }`}
                    data-testid="btn-toggle-active"
                    title={!profile.approved_at ? "Approve configuration first" : ""}
                  >
                    {profile.is_active ? <><Pause size={14} /> Disable</> : <><Play size={14} /> Activate</>}
                  </button>
                </div>
              )}
            </div>

            {/* Create mode */}
            {createMode && selected.canConfigure && (
              <div className="p-6 text-center border-b border-slate-800">
                <div className="inline-flex flex-col items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center">
                    <Plug size={20} className="text-slate-400" />
                  </div>
                  <p className="text-slate-300 text-sm">
                    No configuration profile exists for <strong>{selected.label}</strong>.
                  </p>
                  <p className="text-slate-500 text-xs max-w-xs text-center">
                    Create a profile to begin configuring credentials, ownership, and mappings.
                  </p>
                  <button
                    onClick={createConfig}
                    disabled={saving}
                    className="mt-2 flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all"
                    data-testid="btn-create-config"
                  >
                    {saving ? <RefreshCw size={14} className="animate-spin" /> : <Plug size={14} />}
                    Create Integration Profile
                  </button>
                </div>
              </div>
            )}

            {!selected.canConfigure && (
              <div className="p-6">
                <div className="flex items-start gap-3 p-4 bg-slate-800/40 rounded-lg border border-slate-700/50">
                  <AlertCircle size={16} className="text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-300 mb-1">Configuration Not Available</p>
                    <p className="text-xs text-slate-400">
                      Generic ERP connectors require a concrete endpoint contract and custom adapter implementation
                      before configuration is available. Contact your system integrator.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            {cfgId && !createMode && (
              <>
                <div className="flex border-b border-slate-800 overflow-x-auto" data-testid="config-tabs">
                  {[
                    { id: "overview",    label: "Overview",    icon: Eye },
                    { id: "credentials", label: "Credentials", icon: Lock },
                    { id: "ownership",   label: "Ownership Matrix", icon: Shield },
                    { id: "mappings",    label: "Field Mappings", icon: Grid },
                    { id: "validation",  label: "Validation",  icon: CheckCircle },
                    { id: "approval",    label: "Approval",    icon: FileCheck },
                    ...(selected?.hasConnector ? [
                      { id: "connector", label: "Connector Setup", icon: Wifi },
                      { id: "sync",      label: "Sync & Events",   icon: BarChart2 },
                    ] : []),
                    { id: "events",      label: "Event Monitor", icon: Activity },
                  ].map(({ id, label, icon: TIcon }) => (
                    <button
                      key={id}
                      onClick={() => handleTabChange(id)}
                      className={`flex items-center gap-2 px-5 py-3 text-sm whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === id
                          ? "border-blue-500 text-blue-400 bg-blue-900/10"
                          : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-slate-800/40"
                      }`}
                      data-testid={`tab-${id}`}
                    >
                      <TIcon size={13} />
                      {label}
                    </button>
                  ))}
                </div>

                <div className="p-6">
                  {loadingTab && (
                    <div className="flex items-center gap-2 text-slate-400 text-sm">
                      <RefreshCw size={14} className="animate-spin" /> Loading…
                    </div>
                  )}

                  {/* ── Overview Tab ────────────────────────────────────────── */}
                  {activeTab === "overview" && profile && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-overview">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[
                          { label: "Provider", val: profile.provider },
                          { label: "Category", val: profile.category },
                          { label: "Environment", val: profile.environment || "SANDBOX" },
                          { label: "Status", val: profile.status },
                          { label: "Mapping Version", val: profile.mapping_version ?? 0 },
                          { label: "Approved", val: profile.approved_at ? new Date(profile.approved_at).toLocaleString() : "Pending" },
                        ].map(({ label, val }) => (
                          <div key={label} className="bg-slate-800/40 rounded-lg p-3 border border-slate-700/40">
                            <p className="text-xs text-slate-500 mb-1">{label}</p>
                            <p className="text-sm font-medium text-white">{val}</p>
                          </div>
                        ))}
                      </div>
                      {profile.last_error && (
                        <div className="flex items-start gap-2 p-3 bg-red-900/20 rounded-lg border border-red-700/40">
                          <XCircle size={14} className="text-red-400 mt-0.5 shrink-0" />
                          <p className="text-xs text-red-300">{profile.last_error}</p>
                        </div>
                      )}
                      {profile.certification && (
                        <div className="flex items-start gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/40">
                          <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-white mb-1">{profile.certification.label}</p>
                            <p className="text-xs text-slate-400">{profile.certification.note}</p>
                          </div>
                          <CertBadge tier={profile.certification.tier} />
                        </div>
                      )}
                      {/* Setup guide */}
                      <div className="border border-slate-700/40 rounded-lg overflow-hidden">
                        <p className="px-4 py-2 bg-slate-800/40 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          Setup Checklist
                        </p>
                        {[
                          { label: "Profile Created", done: true },
                          { label: "Credentials Configured", done: profile.secret_reference_present },
                          { label: "Ownership Matrix Set", done: Object.keys(profile.ownership_json || {}).length > 0 },
                          { label: "Field Mappings Saved", done: (profile.mapping_version || 0) > 0 },
                          { label: "Validation Passed", done: profile.last_validated_at != null },
                          { label: "Configuration Approved", done: profile.approved_at != null },
                          { label: "Integration Active", done: profile.is_active },
                        ].map(({ label, done }) => (
                          <div key={label} className="flex items-center gap-3 px-4 py-2.5 border-t border-slate-700/30">
                            {done
                              ? <CheckCircle size={14} className="text-emerald-400 shrink-0" />
                              : <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-600 shrink-0" />
                            }
                            <span className={`text-sm ${done ? "text-slate-200" : "text-slate-500"}`}>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Credentials Tab ──────────────────────────────────────── */}
                  {activeTab === "credentials" && profile && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-credentials">
                      <div className="flex items-start gap-3 p-4 bg-amber-900/10 border border-amber-700/30 rounded-lg">
                        <Lock size={14} className="text-amber-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-amber-300 mb-0.5">Credential Security Policy</p>
                          <p className="text-xs text-amber-400/80">
                            Secret values are never stored in AcademiaOS. Only a reference name (vault key name,
                            environment variable name, or secret store path) is recorded. Actual credentials
                            must be provisioned in your secret management system. (INV-ICC-SECRET-01)
                          </p>
                        </div>
                      </div>

                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/40 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-white">Credential Reference</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            profile.secret_reference_present
                              ? "bg-emerald-900/40 text-emerald-300 border border-emerald-700/40"
                              : "bg-slate-700 text-slate-400"
                          }`}>
                            {profile.secret_reference_present ? "Reference Stored" : "Not Configured"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mb-3">
                          Enter the name of the vault key or environment variable that holds the ERP credentials
                          (e.g., <code className="text-slate-300">ORACLE_ERP_API_KEY</code> or
                          <code className="text-slate-300 ml-1">sap_credentials_ref</code>).
                        </p>
                        {showSecretInput ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="e.g. ORACLE_ERP_API_KEY"
                              value={secretRef}
                              onChange={e => setSecretRef(e.target.value)}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
                              data-testid="input-secret-reference"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={saveCredentialRef}
                                disabled={saving || !secretRef.trim()}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
                                data-testid="btn-save-secret-ref"
                              >
                                {saving ? <RefreshCw size={13} className="animate-spin" /> : <Lock size={13} />}
                                Save Reference
                              </button>
                              <button
                                onClick={() => { setShowSecretInput(false); setSecretRef(""); }}
                                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm transition-all"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowSecretInput(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm transition-all"
                            data-testid="btn-set-credential"
                          >
                            <Lock size={13} />
                            {profile.secret_reference_present ? "Update Credential Reference" : "Set Credential Reference"}
                          </button>
                        )}
                      </div>

                      <div className="bg-slate-800/30 rounded-lg border border-slate-700/40 p-4">
                        <p className="text-sm font-semibold text-white mb-3">Environment</p>
                        <div className="flex gap-3">
                          {["SANDBOX", "PRODUCTION"].map(env => (
                            <button
                              key={env}
                              className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                                (profile.environment || "SANDBOX") === env
                                  ? "bg-blue-900/40 text-blue-300 border-blue-600/60"
                                  : "bg-slate-800 text-slate-400 border-slate-700 hover:border-slate-500"
                              }`}
                              data-testid={`btn-env-${env.toLowerCase()}`}
                            >
                              {env}
                              {env === "PRODUCTION" && (
                                <span className="ml-2 text-xs text-amber-400">(Not Authorized)</span>
                              )}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Production activation requires explicit authorization. SANDBOX is the active environment.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ── Ownership Matrix Tab ─────────────────────────────────── */}
                  {activeTab === "ownership" && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-ownership">
                      <div className="flex items-start gap-3 p-4 bg-blue-900/10 border border-blue-700/30 rounded-lg">
                        <Shield size={14} className="text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-300">
                          Each business object must have exactly one system of record. Assigning an object to
                          "External ERP" means AcademiaOS will defer to the ERP for that function.
                          Incompatible combinations are flagged automatically. (INV-ICC-OWNER-01)
                        </p>
                      </div>

                      <div className="border border-slate-700/40 rounded-lg overflow-hidden">
                        <div className="grid grid-cols-12 px-4 py-2 bg-slate-800/50 border-b border-slate-700/40">
                          <span className="col-span-5 text-xs font-semibold text-slate-400 uppercase tracking-wider">Business Object</span>
                          <span className="col-span-7 text-xs font-semibold text-slate-400 uppercase tracking-wider">System of Record</span>
                        </div>
                        {OWNERSHIP_OBJECTS.map(({ key, label, icon }) => (
                          <div key={key} className="grid grid-cols-12 items-center px-4 py-3 border-b border-slate-700/20 last:border-0">
                            <div className="col-span-5 flex items-center gap-2">
                              <span className="text-base">{icon}</span>
                              <span className="text-sm text-slate-200">{label}</span>
                            </div>
                            <div className="col-span-7 flex gap-2">
                              {OWNER_OPTIONS.map(({ value, label: olabel, color }) => (
                                <button
                                  key={value}
                                  onClick={() => setOwnership(prev => ({ ...prev, [key]: value }))}
                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                    ownership[key] === value
                                      ? `bg-slate-700 ${color} border-slate-500`
                                      : "bg-slate-800/40 text-slate-500 border-slate-700/40 hover:border-slate-600"
                                  }`}
                                  data-testid={`ownership-${key}-${value}`}
                                >
                                  {olabel}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={saveOwnership}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                        data-testid="btn-save-ownership"
                      >
                        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Shield size={14} />}
                        Save Ownership Matrix
                      </button>
                    </div>
                  )}

                  {/* ── Field Mappings Tab ───────────────────────────────────── */}
                  {activeTab === "mappings" && !loadingTab && (
                    <div className="space-y-6" data-testid="tab-content-mappings">
                      <div>
                        <p className="text-sm font-semibold text-white mb-3">Legal Entity & Ledger</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {[
                            { key: "legal_entity",       label: "Legal Entity Code" },
                            { key: "ledger",             label: "Ledger / Book Code" },
                            { key: "cost_center",        label: "Default Cost Center" },
                            { key: "erp_company_code",   label: "ERP Company Code" },
                            { key: "erp_business_unit",  label: "ERP Business Unit" },
                            { key: "fiscal_year_format", label: "Fiscal Year Format" },
                          ].map(({ key, label }) => (
                            <div key={key}>
                              <label className="block text-xs text-slate-400 mb-1">{label}</label>
                              <input
                                type="text"
                                value={mappings[key] || ""}
                                onChange={e => setMappings(prev => ({ ...prev, [key]: e.target.value }))}
                                placeholder="—"
                                className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
                                data-testid={`input-mapping-${key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <p className="text-sm font-semibold text-white mb-3">Account Code Mappings</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {ACCOUNT_KEYS.map((key) => (
                            <div key={key}>
                              <label className="block text-xs text-slate-400 mb-1 capitalize">
                                {key.replace(/_/g, " ")}
                              </label>
                              <input
                                type="text"
                                value={accounts[key] || ""}
                                onChange={e => setAccounts(prev => ({ ...prev, [key]: e.target.value }))}
                                placeholder="Account code"
                                className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
                                data-testid={`input-account-${key}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>

                      <button
                        onClick={saveMappings}
                        disabled={saving}
                        className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                        data-testid="btn-save-mappings"
                      >
                        {saving ? <RefreshCw size={14} className="animate-spin" /> : <Grid size={14} />}
                        Save Field Mappings
                      </button>

                      {profile?.mapping_version > 0 && (
                        <p className="text-xs text-slate-500">
                          Current mapping version: <span className="text-slate-300">v{profile.mapping_version}</span>
                          . Approval resets on every save.
                        </p>
                      )}
                    </div>
                  )}

                  {/* ── Validation Tab ───────────────────────────────────────── */}
                  {activeTab === "validation" && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-validation">
                      <div className="flex items-start gap-3 p-4 bg-slate-800/30 rounded-lg border border-slate-700/40">
                        <Info size={14} className="text-slate-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-slate-400">
                          Deterministic validation — no external calls. Checks ownership completeness,
                          incompatible combinations, mapping completeness, and credential reference.
                          ARGOV-AI-VALUE-GATE-01: no AI invoked.
                        </p>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={runValidation}
                          disabled={testing}
                          className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                          data-testid="btn-run-validation"
                        >
                          {testing ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                          Run Validation
                        </button>
                        <button
                          onClick={testConnection}
                          disabled={testing}
                          className="flex items-center gap-2 px-5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                          data-testid="btn-test-connection"
                        >
                          {testing ? <RefreshCw size={14} className="animate-spin" /> : <Activity size={14} />}
                          Test Connection
                        </button>
                      </div>

                      {valResult && (
                        <div data-testid="validation-results">
                          <div className={`flex items-center gap-3 p-3 rounded-lg mb-4 border ${
                            valResult.validation_result === "PASSED"
                              ? "bg-emerald-900/20 border-emerald-700/40"
                              : valResult.validation_result === "WARNINGS"
                                ? "bg-amber-900/20 border-amber-700/40"
                                : "bg-red-900/20 border-red-700/40"
                          }`}>
                            {valResult.validation_result === "PASSED"
                              ? <CheckCircle size={16} className="text-emerald-400" />
                              : valResult.validation_result === "WARNINGS"
                                ? <AlertTriangle size={16} className="text-amber-400" />
                                : <XCircle size={16} className="text-red-400" />
                            }
                            <div>
                              <p className="font-semibold text-white text-sm">{valResult.validation_result}</p>
                              <p className="text-xs text-slate-400">
                                {valResult.checks_passed} passed · {valResult.checks_failed} failed
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {(valResult.checks || []).map((c, i) => (
                              <div key={i} className="flex items-start gap-3 py-2 px-3 bg-slate-800/40 rounded-lg border border-slate-700/30">
                                {c.passed
                                  ? <CheckCircle size={13} className="text-emerald-400 mt-0.5 shrink-0" />
                                  : <XCircle size={13} className="text-red-400 mt-0.5 shrink-0" />
                                }
                                <div>
                                  <p className="text-xs font-mono text-slate-300">{c.check}</p>
                                  <p className="text-xs text-slate-400 mt-0.5">{c.detail}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Approval Tab ─────────────────────────────────────────── */}
                  {activeTab === "approval" && profile && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-approval">
                      {profile.approved_at ? (
                        <div className="flex items-start gap-3 p-4 bg-emerald-900/10 border border-emerald-700/30 rounded-lg">
                          <FileCheck size={16} className="text-emerald-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-300 mb-1">Configuration Approved</p>
                            <p className="text-xs text-emerald-400/70">
                              Approved at {new Date(profile.approved_at).toLocaleString()}
                            </p>
                            {profile.approval_notes && (
                              <p className="text-xs text-slate-400 mt-1">Notes: {profile.approval_notes}</p>
                            )}
                            <p className="text-xs text-emerald-400/60 mt-1">
                              Approval resets when ownership or mappings are modified.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start gap-3 p-4 bg-amber-900/10 border border-amber-700/30 rounded-lg">
                            <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-semibold text-amber-300 mb-1">Pending Approval</p>
                              <p className="text-xs text-amber-400/80">
                                Configuration must be approved by an authorized admin before activation.
                                Run validation first to confirm all checks pass. (INV-ICC-APPROVE-01)
                              </p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs text-slate-400 mb-1">Approval Notes (optional)</label>
                            <textarea
                              value={approvalNotes}
                              onChange={e => setApprovalNotes(e.target.value)}
                              placeholder="Reviewer notes for this approval…"
                              rows={3}
                              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 resize-none"
                              data-testid="input-approval-notes"
                            />
                          </div>
                          <button
                            onClick={approveConfig}
                            disabled={approving}
                            className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-all disabled:opacity-50"
                            data-testid="btn-approve-config"
                          >
                            {approving ? <RefreshCw size={14} className="animate-spin" /> : <FileCheck size={14} />}
                            Approve Configuration
                          </button>
                        </>
                      )}
                    </div>
                  )}


                  {/* ── Connector Setup Tab (03B) ────────────────────────── */}
                  {activeTab === "connector" && !loadingTab && (
                    <div className="space-y-6" data-testid="tab-content-connector">
                      {profile && (
                        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
                          profile.certification_status === "CONTRACT_TESTED" ? "bg-blue-900/15 border-blue-700/30"
                          : profile.certification_status === "SANDBOX_CERTIFIED" ? "bg-emerald-900/15 border-emerald-700/30"
                          : "bg-amber-900/15 border-amber-700/30"
                        }`}>
                          <Wifi size={14} className="text-amber-400 mt-0.5 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-white mb-0.5">
                              Certification: {profile.certification_status || "NOT_CONFIGURED"}
                            </p>
                            <p className="text-xs text-slate-400">
                              {profile.certification_status === "CONTRACT_TESTED"
                                ? "Contract tests passed. Real sandbox credentials required for SANDBOX_CERTIFIED."
                                : "Configure connection parameters below and run Test Connection to advance status."}
                            </p>
                          </div>
                        </div>
                      )}

                      {connectorSchema && (
                        <div>
                          <p className="text-sm font-semibold text-white mb-3">Connection Parameters</p>
                          <div className="grid grid-cols-2 gap-3">
                            {Object.entries(connectorSchema.fields || {}).map(([key, field]) => {
                              const isSecret = field.type === "secret_ref";
                              const isEnum = field.type === "enum";
                              const isBool = field.type === "bool";
                              const isRequired = (connectorSchema.required || []).includes(key);
                              const value = connectorConfig[key] || "";
                              return (
                                <div key={key}>
                                  <label className="block text-xs text-slate-400 mb-1">
                                    {key.replace(/_/g, " ")}
                                    {isRequired && <span className="text-red-400 ml-1">*</span>}
                                    {isSecret && <span className="ml-1 text-xs text-amber-400">(env var name)</span>}
                                  </label>
                                  {isEnum ? (
                                    <select
                                      value={value}
                                      onChange={e => setConnectorConfig(p => ({ ...p, [key]: e.target.value }))}
                                      className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
                                      data-testid={`input-connector-${key}`}
                                    >
                                      <option value="">— Select —</option>
                                      {(field.values || []).map(v => (<option key={v} value={v}>{v}</option>))}
                                    </select>
                                  ) : isBool ? (
                                    <select
                                      value={String(value || "true")}
                                      onChange={e => setConnectorConfig(p => ({ ...p, [key]: e.target.value === "true" }))}
                                      className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500"
                                      data-testid={`input-connector-${key}`}
                                    >
                                      <option value="true">Yes</option>
                                      <option value="false">No</option>
                                    </select>
                                  ) : (
                                    <input
                                      type="text"
                                      value={value === "[configured]" ? "" : value}
                                      placeholder={value === "[configured]" ? "— already set —" : (field.description || "—")}
                                      onChange={e => setConnectorConfig(p => ({ ...p, [key]: e.target.value }))}
                                      className="w-full px-3 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-blue-500"
                                      data-testid={`input-connector-${key}`}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex gap-3 mt-4">
                            <button
                              onClick={saveConnectorConfig}
                              disabled={savingConnector}
                              className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                              data-testid="btn-save-connector-config"
                            >
                              {savingConnector ? <RefreshCw size={14} className="animate-spin" /> : <Settings size={14} />}
                              Save Parameters
                            </button>
                            <button
                              onClick={testERPConnection}
                              disabled={testingConnector}
                              className="flex items-center gap-2 px-5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg text-sm font-medium disabled:opacity-50"
                              data-testid="btn-test-erp-connection"
                            >
                              {testingConnector ? <RefreshCw size={14} className="animate-spin" /> : <Wifi size={14} />}
                              Test Connection
                            </button>
                          </div>
                        </div>
                      )}

                      {connectorTestResult && (
                        <div className={`p-4 rounded-lg border ${connectorTestResult.connection_result?.success ? "bg-emerald-900/15 border-emerald-700/30" : "bg-red-900/15 border-red-700/30"}`} data-testid="connector-test-result">
                          <div className="flex items-center gap-2 mb-1">
                            {connectorTestResult.connection_result?.success ? <CheckCircle size={14} className="text-emerald-400" /> : <XCircle size={14} className="text-red-400" />}
                            <p className="text-sm font-semibold text-white">
                              {connectorTestResult.connection_result?.success ? "Connection Succeeded" : "Connection Failed"}
                            </p>
                            <span className="ml-auto text-xs text-slate-400">{connectorTestResult.connection_result?.latency_ms}ms</span>
                          </div>
                          {connectorTestResult.connection_result?.error_message && (
                            <p className="text-xs text-red-300 mt-1">{connectorTestResult.connection_result.error_message}</p>
                          )}
                          {connectorTestResult.connection_result?.edition && (
                            <p className="text-xs text-slate-400 mt-1">Edition: {connectorTestResult.connection_result.edition}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-2">{connectorTestResult.note}</p>
                        </div>
                      )}

                      {capabilities && (
                        <div>
                          <p className="text-sm font-semibold text-white mb-2">Provider Capabilities</p>
                          <div className="grid grid-cols-2 gap-2">
                            {[...(capabilities.supported || []), ...(capabilities.requires_authorization || []), ...(capabilities.unsupported || [])].map(cap => {
                              const isSupported = (capabilities.supported || []).includes(cap);
                              const requiresAuth = (capabilities.requires_authorization || []).includes(cap);
                              return (
                                <div key={cap} className="flex items-center gap-2 px-3 py-2 bg-slate-800/40 rounded-lg border border-slate-700/30">
                                  {isSupported ? <CheckCircle size={12} className="text-emerald-400 shrink-0" />
                                  : requiresAuth ? <Shield size={12} className="text-amber-400 shrink-0" />
                                  : <XCircle size={12} className="text-red-400 shrink-0" />}
                                  <span className="text-xs text-slate-300 capitalize">{cap.replace(/_/g, " ")}</span>
                                  {requiresAuth && <span className="text-xs text-amber-400 ml-auto">auth required</span>}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {certSteps && (
                        <div>
                          <p className="text-sm font-semibold text-white mb-2">Certification Roadmap</p>
                          <div className="border border-slate-700/40 rounded-lg overflow-hidden" data-testid="certification-roadmap">
                            {(certSteps.steps?.completed || []).map((s, i) => (
                              <div key={i} className="flex items-start gap-3 px-4 py-2.5 border-b border-slate-700/20">
                                <CheckCircle size={12} className="text-emerald-400 shrink-0 mt-0.5" />
                                <div><p className="text-xs font-medium text-slate-200">{s.label}</p><p className="text-xs text-slate-500">{s.detail}</p></div>
                              </div>
                            ))}
                            {(certSteps.steps?.pending || []).map((s, i) => (
                              <div key={i} className="flex items-start gap-3 px-4 py-2.5 border-b border-slate-700/20 last:border-0">
                                {s.status === "REQUIRES_SANDBOX_CREDENTIALS"
                                  ? <WifiOff size={12} className="text-amber-400 shrink-0 mt-0.5" />
                                  : s.status === "REQUIRES_OWNER_AUTHORIZATION"
                                    ? <Shield size={12} className="text-red-400 shrink-0 mt-0.5" />
                                    : <div className="w-3 h-3 rounded-full border-2 border-slate-600 shrink-0 mt-0.5" />}
                                <div><p className="text-xs font-medium text-slate-400">{s.label}</p><p className="text-xs text-slate-600">{s.detail}</p></div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Sync & Events Tab (03B) ───────────────────────────── */}
                  {activeTab === "sync" && !loadingTab && (
                    <div className="space-y-5" data-testid="tab-content-sync">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-white">Connector Operation Events</p>
                        <button onClick={() => loadTabData("sync")} className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1" data-testid="btn-refresh-connector-events">
                          <RefreshCw size={11} /> Refresh
                        </button>
                      </div>
                      <div className="flex items-start gap-3 p-3 bg-blue-900/10 border border-blue-700/30 rounded-lg">
                        <Info size={12} className="text-blue-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-blue-300">
                          ACCEPTED/SUBMITTED ≠ POSTED. ERP uses asynchronous batch posting. POSTED requires explicit ERP acknowledgement. (INV-03B-ASYNC-01)
                        </p>
                      </div>
                      {connectorEvents.length === 0 ? (
                        <p className="text-sm text-slate-500 py-6 text-center">No connector events yet.</p>
                      ) : (
                        <div className="border border-slate-700/40 rounded-lg overflow-hidden" data-testid="connector-events-table">
                          <div className="grid grid-cols-12 px-4 py-2 bg-slate-800/50 border-b border-slate-700/40 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            <span className="col-span-2">State</span><span className="col-span-2">Operation</span><span className="col-span-3">Correlation</span><span className="col-span-2">Ext Doc</span><span className="col-span-2">Time</span><span className="col-span-1"></span>
                          </div>
                          {connectorEvents.slice(0, 20).map(ev => (
                            <div key={ev.id} className="grid grid-cols-12 items-center px-4 py-2.5 border-t border-slate-700/20 text-xs">
                              <div className="col-span-2 flex items-center gap-1.5">
                                <span className={`w-2 h-2 rounded-full shrink-0 ${ev.document_state === "POSTED" ? "bg-emerald-400" : ev.document_state === "ERROR" ? "bg-red-400" : ev.document_state === "ACCEPTED" || ev.document_state === "SUBMITTED" ? "bg-blue-400" : "bg-amber-400"}`} />
                                <span className="text-slate-300">{ev.document_state}</span>
                              </div>
                              <span className="col-span-2 font-mono text-slate-400 truncate">{ev.operation?.replace("_SYNC", "")}</span>
                              <span className="col-span-3 font-mono text-slate-500 text-xs truncate">{ev.correlation_id?.slice(0, 10)}…</span>
                              <span className="col-span-2 text-slate-400 truncate">{ev.external_document_id || "—"}</span>
                              <span className="col-span-2 text-slate-500">{ev.created_at ? new Date(ev.created_at).toLocaleString() : "—"}</span>
                              <div className="col-span-1">
                                {["ERROR", "PENDING_ASYNC"].includes(ev.document_state) && (
                                  <button onClick={() => retryConnectorEvent(ev.id)} className="text-amber-400 hover:text-amber-300" title="Retry" data-testid={`btn-retry-connector-event-${ev.id}`}><RotateCcw size={12} /></button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {connectorEvents.filter(e => e.error_message).length > 0 && (
                        <div>
                          <p className="text-sm font-semibold text-white mb-2">Failed Event Details</p>
                          {connectorEvents.filter(e => e.error_message).slice(0, 5).map(ev => (
                            <div key={ev.id} className="mb-2 p-3 bg-red-900/15 rounded-lg border border-red-700/30">
                              <div className="flex items-center gap-2 mb-1"><XCircle size={12} className="text-red-400" /><span className="text-xs font-mono text-slate-300">{ev.error_code}</span></div>
                              <p className="text-xs text-red-300">{ev.error_message}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}


                  {/* ── Event Monitor Tab ────────────────────────────────────── */}
                  {activeTab === "events" && !loadingTab && (
                    <div className="space-y-6" data-testid="tab-content-events">
                      {/* Execution History */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-sm font-semibold text-white">Execution History</p>
                          <button
                            onClick={() => loadTabData("events")}
                            className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
                          >
                            <RefreshCw size={11} /> Refresh
                          </button>
                        </div>
                        {execHistory.length === 0 ? (
                          <p className="text-sm text-slate-500 py-4 text-center">No executions recorded.</p>
                        ) : (
                          <div className="border border-slate-700/40 rounded-lg overflow-hidden">
                            {execHistory.slice(0, 10).map((exec, i) => (
                              <div key={exec.id} className={`flex items-center gap-4 px-4 py-2.5 text-xs ${i > 0 ? "border-t border-slate-700/30" : ""}`}>
                                <span className={`w-2 h-2 rounded-full shrink-0 ${exec.status === "COMPLETED" ? "bg-emerald-400" : "bg-red-400"}`} />
                                <span className="font-mono text-slate-300 w-36 truncate">{exec.operation}</span>
                                <span className={`${exec.status === "COMPLETED" ? "text-emerald-400" : "text-red-400"}`}>{exec.status}</span>
                                <span className="text-slate-500 ml-auto">
                                  {exec.started_at ? new Date(exec.started_at).toLocaleString() : "—"}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Inbox Events */}
                      <div>
                        <p className="text-sm font-semibold text-white mb-3">Webhook / Inbox Events</p>
                        {inboxEvents.length === 0 ? (
                          <p className="text-sm text-slate-500 py-4 text-center">No inbox events.</p>
                        ) : (
                          <div className="border border-slate-700/40 rounded-lg overflow-hidden" data-testid="inbox-events-table">
                            <div className="grid grid-cols-12 px-4 py-2 bg-slate-800/50 border-b border-slate-700/40 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                              <span className="col-span-2">Status</span>
                              <span className="col-span-2">Provider</span>
                              <span className="col-span-3">Event Type</span>
                              <span className="col-span-2">Retries</span>
                              <span className="col-span-2">Received</span>
                              <span className="col-span-1">Action</span>
                            </div>
                            {inboxEvents.slice(0, 20).map((ev) => (
                              <div key={ev.id} className="grid grid-cols-12 items-center px-4 py-2.5 border-t border-slate-700/20 text-xs">
                                <div className="col-span-2 flex items-center gap-1.5">
                                  <span className={`w-2 h-2 rounded-full ${
                                    ev.processing_status === "COMPLETED" ? "bg-emerald-400"
                                    : ev.processing_status === "FAILED" ? "bg-red-400"
                                    : ev.processing_status === "PENDING" ? "bg-amber-400"
                                    : "bg-slate-500"
                                  }`} />
                                  <span className="text-slate-300">{ev.processing_status}</span>
                                </div>
                                <span className="col-span-2 font-mono text-slate-400">{ev.provider}</span>
                                <span className="col-span-3 text-slate-300">{ev.event_type}</span>
                                <span className="col-span-2 text-slate-400">{ev.retry_count || 0}</span>
                                <span className="col-span-2 text-slate-500">
                                  {ev.received_at ? new Date(ev.received_at).toLocaleString() : "—"}
                                </span>
                                <div className="col-span-1">
                                  {["FAILED", "REJECTED"].includes(ev.processing_status) && (
                                    <button
                                      onClick={() => retryEvent(ev.id)}
                                      className="flex items-center gap-1 text-amber-400 hover:text-amber-300"
                                      title="Retry event"
                                      data-testid={`btn-retry-event-${ev.id}`}
                                    >
                                      <RotateCcw size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Not configured prompt when nothing selected */}
        {!selected && (
          <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-700/50 rounded-xl">
            <div className="w-14 h-14 rounded-xl bg-slate-800 flex items-center justify-center mb-4">
              <Plug size={22} className="text-slate-500" />
            </div>
            <p className="text-slate-300 font-medium mb-1">Select a Provider</p>
            <p className="text-slate-500 text-sm max-w-xs">
              Choose an integration provider above to configure credentials, ownership, mappings, and activation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
